import React from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Download, Upload, RefreshCw, Hotel, BedDouble, CalendarCheck, CheckCircle2, XCircle } from 'lucide-react';
import { hospitalityApi } from '../config/api';
import { useAuth } from '../context/AuthContext';

const field = 'h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-slate-400';
const label = 'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500';

function previewRowDisplay(row = {}) {
  const hotel = row.data?.hotel || {};
  const room = row.data?.room || {};
  const raw = row.raw || {};
  return {
    row_number: row.row_number,
    hotel_code: row.hotel_code || hotel.HOTEL_CODE || raw.hotel_code || '-',
    hotel_name: row.hotel_name || hotel.HOTEL_NAME || raw.hotel_name || raw.HOTEL_NAME || '-',
    hotel_location: row.hotel_location || hotel.HOTEL_LOCATION || raw.hotel_location || '-',
    hotel_email: row.hotel_email || hotel.HOTEL_EMAIL || raw.hotel_email || '-',
    hotel_bank_account: row.hotel_bank_account || hotel.HOTEL_BANKACCOUNT || raw.hotel_bank_account || '-',
    hotel_bank_name: row.hotel_bank_name || hotel.HOTEL_BANKNAME || raw.hotel_bank_name || '-',
    room_code: row.room_code || room.ROOMTYPE_CODE || raw.room_code || '-',
    room_name: row.room_name || room.ROOMTYPE_NAME || raw.room_name || raw.ROOMTYPE_NAME || '-',
    room_base_occupancy: row.room_base_occupancy ?? room.ROOMTYPE_BASEOCCUPANCY ?? raw.room_base_occupancy ?? '-',
    room_max_occupancy: row.room_max_occupancy ?? room.ROOMTYPE_MAXOCCUPANCY ?? raw.room_max_occupancy ?? '-',
    status: row.status,
    errors: row.errors || [],
    warnings: row.warnings || [],
  };
}

const hotelFields = [
  ['hotel_code', 'Hotel Code'],
  ['hotel_name', 'Hotel Name'],
  ['hotel_star', 'Star'],
  ['hotel_location', 'Location'],
  ['hotel_contact_person', 'Contact Person'],
  ['hotel_phone', 'Phone'],
  ['hotel_email', 'Email'],
  ['hotel_bank_account', 'Nomor Rekening'],
  ['hotel_bank_name', 'Bank'],
  ['hotel_booking_url', 'Booking URL'],
  ['hotel_publish_rate', 'Publish Rate'],
  ['hotel_room_option', 'Room Option'],
  ['hotel_discount', 'Discount'],
  ['hotel_fixed_price', 'Fixed Price'],
  ['hotel_status', 'Status'],
  ['room_code', 'Room Code'],
  ['room_name', 'Room Name'],
  ['room_base_occupancy', 'Base Occupancy'],
  ['room_max_occupancy', 'Max Occupancy'],
  ['room_discount_price', 'Room Discount Price'],
  ['room_fixed_price', 'Room Fixed Price'],
];

function normalizeAdminStatus(user, selectedRole = null) {
  return String(selectedRole || user?.admin_status || user?.adminStatus || user?.role || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
}

function isHinUser(user, selectedRole = null) {
  const adminStatus = normalizeAdminStatus(user, selectedRole);
  const org = String(user?.organization_code || user?.organizationCode || '').trim().toLowerCase();
  return adminStatus === 'admin_inj' || (adminStatus === 'admin_member' && org === 'hin');
}

function fmtDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function Badge({ value }) {
  const v = value || 'UNKNOWN';
  const tone = v === 'ACTIVE' || v === 'HOLD' || v === 'AVAILABLE' || v === 'SUCCESS'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : v === 'PENDING' || v === 'WARNING' || v === 'PREVIEW_WITH_ERRORS'
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-slate-50 text-slate-600 border-slate-200';
  return <span className={`inline-flex rounded-md border px-2 py-1 text-[11px] font-bold ${tone}`}>{v}</span>;
}

function Metric({ label: title, value, Icon }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] font-bold uppercase text-slate-500">{title}</div>
          <div className="mt-2 text-2xl font-bold text-slate-900">{value ?? '-'}</div>
        </div>
        <Icon className="h-5 w-5 text-slate-500" />
      </div>
    </div>
  );
}

const defaultSections = [
  ['master', 'Master Hotel'],
  ['import', 'Import Hotel'],
  ['availability', 'Permintaan Ketersediaan'],
  ['history', 'Riwayat Import'],
];

export default function HinHotelMaster({
  embedded = false,
  initialActive,
  sections = defaultSections,
  showSectionTabs = true,
}) {
  const { user, selectedRole } = useAuth();
  const allowed = isHinUser(user, selectedRole);
  const location = useLocation();
  const navigate = useNavigate();
  const fallbackActive = initialActive || (location.pathname.includes('/availability') ? 'availability' : 'master');
  const [active, setActive] = React.useState(fallbackActive);
  const [hotels, setHotels] = React.useState([]);
  const [requests, setRequests] = React.useState([]);
  const [history, setHistory] = React.useState([]);
  const [selectedHotel, setSelectedHotel] = React.useState(null);
  const [availabilityHotel, setAvailabilityHotel] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [file, setFile] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [notice, setNotice] = React.useState('');
  const [error, setError] = React.useState('');
  const [filters, setFilters] = React.useState({ search: '', hotel_location: '', star: '', status: '' });
  const [hotelForm, setHotelForm] = React.useState({
    hotel_code: '',
    hotel_name: '',
    hotel_star: '',
    hotel_location: '',
    hotel_contact_person: '',
    hotel_phone: '',
    hotel_email: '',
    hotel_bank_account: '',
    hotel_bank_name: '',
    hotel_booking_url: '',
    hotel_publish_rate: '0',
    hotel_room_option: '',
    hotel_discount: '',
    hotel_fixed_price: '',
    hotel_status: '1',
    room_code: 'DEFAULT',
    room_name: 'Default Room',
    room_base_occupancy: 2,
    room_max_occupancy: 2,
    room_discount_price: '',
    room_fixed_price: '',
  });
  const [availabilityForm, setAvailabilityForm] = React.useState({ hotel_id: '', room_type_id: '', booking_id: '', check_in_date: '', check_out_date: '', room_qty: 1, adult_qty: 1, child_qty: 0, guest_name: '', customer_email: '', notes: '' });

  const load = React.useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    setError('');
    try {
      const [hotelRes, reqRes, histRes] = await Promise.all([
        hospitalityApi.get('/hin/hotels', { params: filters }),
        hospitalityApi.get('/hin/availability'),
        hospitalityApi.get('/hin/import/history'),
      ]);
      setHotels(hotelRes.data?.data || []);
      setRequests(reqRes.data?.data || []);
      setHistory(histRes.data?.data || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Gagal memuat data HIN.');
    } finally {
      setLoading(false);
    }
  }, [allowed, filters]);

  React.useEffect(() => { load(); }, [load]);

  React.useEffect(() => {
    if (initialActive) {
      setActive(initialActive);
    }
  }, [initialActive]);

  React.useEffect(() => {
    if (embedded) return;
    if (location.pathname.includes('/availability')) setActive('availability');
    else if (active === 'availability') setActive('master');
  }, [location.pathname, active, embedded]);

  React.useEffect(() => {
    const loadHotelDetail = async () => {
      if (!availabilityForm.hotel_id) {
        setAvailabilityHotel(null);
        return;
      }
      try {
        const res = await hospitalityApi.get(`/hin/hotels/${availabilityForm.hotel_id}`);
        setAvailabilityHotel(res.data?.data || null);
      } catch (_) {
        setAvailabilityHotel(null);
      }
    };
    loadHotelDetail();
  }, [availabilityForm.hotel_id]);

  React.useEffect(() => {
    if (!selectedHotel && !previewOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (selectedHotel) setSelectedHotel(null);
      else if (previewOpen) setPreviewOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedHotel, previewOpen]);

  const previewImport = async () => {
    if (!file) { setError('Pilih file .xlsx terlebih dahulu.'); return; }
    setLoading(true);
    setError('');
    setNotice('');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await hospitalityApi.post('/hin/import/preview', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const data = res.data?.data || null;
      setPreview(data);
      setPreviewOpen(Boolean(data));
      setNotice(`Preview siap: ${data?.valid_rows || 0} valid, ${data?.invalid_rows || 0} invalid.`);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const confirmImport = async () => {
    if (!preview?.batch_id) return;
    setLoading(true);
    setError('');
    setNotice('');
    try {
      const res = await hospitalityApi.post(`/hin/import/${preview.batch_id}/confirm`);
      setNotice(
        `Import selesai: ${res.data?.data?.hotels_created || 0} hotel baru, ${res.data?.data?.hotels_updated || 0} hotel diupdate, `
        + `${res.data?.data?.rooms_created || 0} room type baru, ${res.data?.data?.rooms_updated || 0} room type diupdate.`
      );
      setPreview(null);
      setPreviewOpen(false);
      setFile(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    setError('');
    setNotice('');
    try {
      const res = await hospitalityApi.get('/hin/hotels/template', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'HIN_Hotel_Master_Template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Gagal mengunduh template Excel.');
    }
  };

  const saveHotel = async () => {
    setLoading(true);
    setError('');
    setNotice('');
    try {
      await hospitalityApi.post('/hin/hotels', hotelForm);
      setNotice('Master Hotel berhasil disimpan ke tx_hotel dan tx_hotel_roomtypes.');
      setHotelForm({ ...hotelForm, hotel_code: '', hotel_name: '', hotel_location: '' });
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const createAvailability = async () => {
    setLoading(true);
    setError('');
    setNotice('');
    try {
      await hospitalityApi.post('/hin/availability', availabilityForm);
      setNotice('Permintaan ketersediaan dibuat.');
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const confirmAvailability = async (request, decision) => {
    const notes = window.prompt(decision === 'AVAILABLE' ? 'Catatan untuk kamar tersedia:' : 'Alasan kamar tidak tersedia:', request.notes || '');
    setLoading(true);
    setError('');
    setNotice('');
    try {
      await hospitalityApi.post(`/hin/availability/${request.id}/confirm`, { decision, notes });
      setNotice(decision === 'AVAILABLE' ? 'Kamar dikonfirmasi tersedia dan masuk HOLD.' : 'Kamar dikonfirmasi tidak tersedia.');
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const openHotelDetail = async (hotelId) => {
    setLoading(true);
    setError('');
    try {
      const res = await hospitalityApi.get(`/hin/hotels/${hotelId}`);
      setSelectedHotel(res.data?.data || null);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Gagal memuat detail hotel.');
    } finally {
      setLoading(false);
    }
  };

  const selectedForRequest = hotels.find((h) => String(h.hotel_id) === String(availabilityForm.hotel_id));

  const availableSections = sections?.length ? sections : defaultSections;

  if (!allowed) {
    return (
      <div className={`${embedded ? '' : 'mx-auto max-w-3xl'} rounded-lg border border-red-100 bg-white p-8`}>
        <h1 className="text-xl font-bold">Akses dibatasi</h1>
        <p className="mt-2 text-slate-600">Master Hotel HIN hanya tersedia untuk Admin INJ dan Admin Member HIN.</p>
      </div>
    );
  }

  return (
    <div className={`${embedded ? '' : 'mx-auto max-w-7xl'} space-y-5`}>
      {!embedded && (
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Hospitality</div>
            <h1 className="mt-1 text-2xl font-bold text-slate-900">Master Hotel HIN</h1>
            <p className="mt-1 text-sm text-slate-600">Excel to existing PRIME Hotel Master, room type, availability confirmation, and manual HIN email workflow.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={downloadTemplate} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold"><Download className="h-4 w-4" />Template Excel</button>
            <button onClick={load} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold"><RefreshCw className="h-4 w-4" />Refresh</button>
          </div>
        </div>
      )}

      {notice && <div className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-700">{notice}</div>}
      {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      {showSectionTabs && availableSections.length > 1 && (
        <div className="flex flex-wrap gap-1 border-b border-slate-200">
          {availableSections.map(([key, title]) => (
            <button
              key={key}
              onClick={() => {
                setActive(key);
                if (!embedded) {
                  if (key === 'availability') navigate('/admin/hospitality/availability');
                  else if (location.pathname.includes('/availability')) navigate('/admin/hospitality/hotel-master');
                }
              }}
              className={`px-3 py-2 text-sm font-bold ${active === key ? 'border-b-2 border-[#12284A] text-[#12284A]' : 'text-slate-500'}`}
            >
              {title}
            </button>
          ))}
        </div>
      )}

      {active === 'master' && (
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-3">
            <Metric label="Hotel" value={hotels.length} Icon={Hotel} />
            <Metric label="Room Types" value={hotels.reduce((s, h) => s + Number(h.rooms_count || 0), 0)} Icon={BedDouble} />
            <Metric label="Availability Requests" value={requests.length} Icon={CalendarCheck} />
          </div>
          <div className="grid gap-3 lg:grid-cols-4">
            {Object.keys(filters).map((key) => (
              <input key={key} className={field} value={filters[key]} onChange={(e) => setFilters({ ...filters, [key]: e.target.value })} placeholder={key === 'search' ? 'Search hotel' : key} />
            ))}
          </div>
          <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
              <table className="w-full min-w-[860px] text-left text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Hotel Code</th>
                    <th className="px-4 py-3">Hotel Name</th>
                    <th className="px-4 py-3">Location</th>
                    <th className="px-4 py-3">Star</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Rekening</th>
                    <th className="px-4 py-3">Rooms</th>
                    <th className="px-4 py-3">Publish Rate</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Last Updated</th>
                    <th className="px-4 py-3">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? <tr><td colSpan="11" className="px-4 py-8 text-center text-slate-500">Memuat data...</td></tr> : hotels.length ? hotels.map((h) => (
                    <tr key={h.hotel_id}>
                      <td className="px-4 py-3 font-bold">{h.hotel_code}</td>
                      <td className="px-4 py-3">{h.hotel_name}</td>
                      <td className="px-4 py-3">{h.hotel_location || h.city || '-'}</td>
                      <td className="px-4 py-3">{h.star_rating || '-'}</td>
                      <td className="px-4 py-3 text-xs">{h.hotel_email || h.HOTEL_EMAIL || '-'}</td>
                      <td className="px-4 py-3 text-xs">
                        {[h.hotel_bank_name || h.HOTEL_BANKNAME, h.hotel_bank_account || h.HOTEL_BANKACCOUNT || h.bank_account]
                          .filter(Boolean).join(' · ') || '-'}
                      </td>
                      <td className="px-4 py-3">{h.rooms_count || 0}</td>
                      <td className="px-4 py-3">{h.HOTEL_PUBLISHRATE ?? '-'}</td>
                      <td className="px-4 py-3"><Badge value={h.status} /></td>
                      <td className="px-4 py-3">{fmtDate(h.updated_at)}</td>
                      <td className="px-4 py-3"><button type="button" onClick={() => openHotelDetail(h.hotel_id)} className="text-xs font-bold text-[#12284A]">View</button></td>
                    </tr>
                  )) : <tr><td colSpan="11" className="px-4 py-10 text-center text-slate-500">Belum ada data hotel HIN.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="mb-3 font-bold">Tambah / Update Hotel</div>
              <div className="space-y-3">
                {hotelFields.map(([key, title]) => (
                  <label key={key} className="block">
                    <span className={label}>{title}</span>
                    <input className={`${field} w-full`} value={hotelForm[key]} onChange={(e) => setHotelForm({ ...hotelForm, [key]: e.target.value })} />
                  </label>
                ))}
                <button disabled={loading || !hotelForm.hotel_code || !hotelForm.hotel_name || !hotelForm.room_code || !hotelForm.room_name} onClick={saveHotel} className="w-full rounded-lg bg-[#12284A] px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-40">Simpan Hotel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedHotel && createPortal(
        <div
          className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/55 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setSelectedHotel(null)}
        >
          <div
            className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-slate-100 bg-white px-5 py-4">
              <div>
                <div className="text-lg font-bold text-slate-900">{selectedHotel.hotel_name}</div>
                <div className="mt-1 text-sm text-slate-500">
                  {selectedHotel.hotel_code}
                  {' · '}
                  {selectedHotel.hotel_location || selectedHotel.city || selectedHotel.HOTEL_LOCATION || '-'}
                  {' · '}
                  Star {selectedHotel.star_rating || selectedHotel.HOTEL_STAR || '-'}
                </div>
                <div className="mt-2 space-y-1 text-sm text-slate-600">
                  <div>Email: {selectedHotel.hotel_email || selectedHotel.HOTEL_EMAIL || '-'}</div>
                  <div>
                    Rekening: {[selectedHotel.hotel_bank_name || selectedHotel.HOTEL_BANKNAME, selectedHotel.hotel_bank_account || selectedHotel.HOTEL_BANKACCOUNT].filter(Boolean).join(' · ') || '-'}
                  </div>
                </div>
              </div>
              <button type="button" onClick={() => setSelectedHotel(null)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600">Close</button>
            </div>
            <div className="px-5 py-4">
              <div className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-500">
                Room Types ({(selectedHotel.rooms || []).length})
              </div>
              <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
                {(selectedHotel.rooms || []).length ? (selectedHotel.rooms || []).map((r) => (
                  <div key={r.room_type_id} className="rounded-md border border-slate-200 p-3">
                    <div className="font-bold text-slate-900">{r.room_name}</div>
                    <div className="text-sm text-slate-500">{r.room_code}</div>
                    <div className="mt-1 text-xs text-slate-500">Occupancy {r.base_occupancy || '-'} / {r.max_occupancy || '-'}</div>
                  </div>
                )) : (
                  <div className="col-span-full rounded-md border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">
                    Belum ada room type untuk hotel ini.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {previewOpen && preview && createPortal(
        <div
          className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/55 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setPreviewOpen(false)}
        >
          <div
            className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
              <div>
                <div className="text-lg font-bold text-slate-900">Preview Import Hotel Master</div>
                <div className="mt-1 text-sm text-slate-500">{preview.filename || 'Excel'} · {preview.total_rows || 0} baris</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => setPreviewOpen(false)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600">Tutup</button>
                <button type="button" disabled={loading} onClick={confirmImport} className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">Confirm Import</button>
              </div>
            </div>
            <div className="grid gap-3 border-b border-slate-100 p-4 md:grid-cols-4">
              <Metric label="Total Rows" value={preview.total_rows} Icon={Upload} />
              <Metric label="Valid" value={preview.valid_rows} Icon={CheckCircle2} />
              <Metric label="Warnings" value={preview.warning_rows} Icon={CalendarCheck} />
              <Metric label="Invalid" value={preview.invalid_rows} Icon={XCircle} />
            </div>
            <div className="overflow-auto">
              <table className="w-full min-w-[1080px] text-left text-sm">
                <thead className="sticky top-0 bg-slate-50 text-[11px] uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Row</th>
                    <th className="px-4 py-3">Hotel</th>
                    <th className="px-4 py-3">Location</th>
                    <th className="px-4 py-3">Room Type</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Rekening</th>
                    <th className="px-4 py-3">Occupancy</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Message</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(preview.rows || []).map((row) => {
                    const r = previewRowDisplay(row);
                    return (
                      <tr key={r.row_number}>
                        <td className="px-4 py-3">{r.row_number}</td>
                        <td className="px-4 py-3">
                          <div className="font-bold text-slate-900">{r.hotel_name}</div>
                          <div className="text-xs text-slate-500">{r.hotel_code}</div>
                        </td>
                        <td className="px-4 py-3">{r.hotel_location}</td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-slate-800">{r.room_name}</div>
                          <div className="text-xs text-slate-500">{r.room_code}</div>
                        </td>
                        <td className="px-4 py-3 text-xs">{r.hotel_email}</td>
                        <td className="px-4 py-3 text-xs">
                          {[r.hotel_bank_name, r.hotel_bank_account].filter((v) => v && v !== '-').join(' · ') || '-'}
                        </td>
                        <td className="px-4 py-3 text-xs">{r.room_base_occupancy} / {r.room_max_occupancy}</td>
                        <td className="px-4 py-3"><Badge value={r.status} /></td>
                        <td className="px-4 py-3 text-xs">{[...r.errors, ...r.warnings].join(', ') || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {active === 'import' && (
        <div className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="mb-3 font-bold">Import Hotel Master</div>
            <p className="mb-3 text-sm text-slate-600">
              Template Excel mencakup <span className="font-semibold">hotel_email</span>, <span className="font-semibold">hotel_bank_account</span>, dan <span className="font-semibold">hotel_bank_name</span> per hotel.
              Setelah Preview, hasil tampil di modal (nama hotel & room type, bukan hanya kode).
            </p>
            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={downloadTemplate} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700">
                <Download className="h-4 w-4" />Template Excel
              </button>
              <input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)} className={field} />
              <button disabled={loading || !file} onClick={previewImport} className="inline-flex items-center gap-2 rounded-lg bg-[#12284A] px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"><Upload className="h-4 w-4" />Preview Import</button>
              {preview && !previewOpen && (
                <button type="button" onClick={() => setPreviewOpen(true)} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700">
                  Lihat Preview Lagi
                </button>
              )}
              {preview && (
                <button disabled={loading} onClick={confirmImport} className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
                  Confirm Import
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {active === 'availability' && (
        <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="w-full min-w-[980px] text-left text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase text-slate-500"><tr><th className="px-4 py-3">Request</th><th className="px-4 py-3">Booking</th><th className="px-4 py-3">Hotel</th><th className="px-4 py-3">Room Type</th><th className="px-4 py-3">Check In</th><th className="px-4 py-3">Check Out</th><th className="px-4 py-3">Rooms</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Hold Until</th><th className="px-4 py-3">Action</th></tr></thead>
              <tbody className="divide-y divide-slate-100">{requests.length ? requests.map((r) => <tr key={r.id}><td className="px-4 py-3 font-bold">{r.request_code}</td><td className="px-4 py-3">{r.booking_id || '-'}</td><td className="px-4 py-3">{r.hotel_name}</td><td className="px-4 py-3">{r.room_name}</td><td className="px-4 py-3">{r.check_in_date}</td><td className="px-4 py-3">{r.check_out_date}</td><td className="px-4 py-3">{r.room_qty}</td><td className="px-4 py-3"><Badge value={r.status} /></td><td className="px-4 py-3">{fmtDate(r.hold_until)}</td><td className="px-4 py-3"><div className="flex gap-2"><button disabled={r.status !== 'PENDING'} onClick={() => confirmAvailability(r, 'AVAILABLE')} className="text-xs font-bold text-emerald-700 disabled:text-slate-400">Tersedia</button><button disabled={r.status !== 'PENDING'} onClick={() => confirmAvailability(r, 'NOT_AVAILABLE')} className="text-xs font-bold text-red-700 disabled:text-slate-400">Tidak Tersedia</button></div></td></tr>) : <tr><td colSpan="10" className="px-4 py-10 text-center text-slate-500">Belum ada permintaan ketersediaan kamar.</td></tr>}</tbody>
            </table>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="mb-3 font-bold">Buat Permintaan</div>
            <div className="space-y-3">
              <label><span className={label}>Hotel</span><select className={`${field} w-full`} value={availabilityForm.hotel_id} onChange={(e) => setAvailabilityForm({ ...availabilityForm, hotel_id: e.target.value, room_type_id: '' })}><option value="">Pilih Hotel</option>{hotels.map((h) => <option key={h.hotel_id} value={h.hotel_id}>{h.hotel_code} - {h.hotel_name}</option>)}</select></label>
              <label><span className={label}>Tipe Kamar</span><select className={`${field} w-full`} value={availabilityForm.room_type_id} onChange={(e) => setAvailabilityForm({ ...availabilityForm, room_type_id: e.target.value })}><option value="">Pilih Tipe Kamar</option>{(selectedForRequest?.rooms || availabilityHotel?.rooms || []).map((r) => <option key={r.room_type_id} value={r.room_type_id}>{r.room_code} - {r.room_name}</option>)}</select></label>
              {['booking_id', 'check_in_date', 'check_out_date', 'room_qty', 'adult_qty', 'child_qty', 'guest_name', 'customer_email'].map((key) => <label key={key}><span className={label}>{key.replace(/_/g, ' ')}</span><input type={key.includes('date') ? 'date' : key.includes('email') ? 'email' : 'text'} className={`${field} w-full`} value={availabilityForm[key]} onChange={(e) => setAvailabilityForm({ ...availabilityForm, [key]: e.target.value })} /></label>)}
              <button disabled={loading || !availabilityForm.hotel_id || !availabilityForm.room_type_id || !availabilityForm.check_in_date || !availabilityForm.check_out_date} onClick={createAvailability} className="w-full rounded-lg bg-[#12284A] px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-40">Buat Request</button>
            </div>
          </div>
        </div>
      )}

      {active === 'history' && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <table className="w-full min-w-[860px] text-left text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500"><tr><th className="px-4 py-3">Batch</th><th className="px-4 py-3">File</th><th className="px-4 py-3">Rows</th><th className="px-4 py-3">Hotels</th><th className="px-4 py-3">Rooms</th><th className="px-4 py-3">Skipped</th><th className="px-4 py-3">Uploaded By</th><th className="px-4 py-3">Completed</th><th className="px-4 py-3">Status</th></tr></thead>
            <tbody className="divide-y divide-slate-100">{history.length ? history.map((b) => <tr key={b.id}><td className="px-4 py-3 font-mono text-xs">{b.id}</td><td className="px-4 py-3">{b.filename}</td><td className="px-4 py-3">{b.total_rows}</td><td className="px-4 py-3">{Number(b.hotels_created || 0) + Number(b.hotels_updated || 0)}</td><td className="px-4 py-3">{Number(b.rooms_created || 0) + Number(b.rooms_updated || 0)}</td><td className="px-4 py-3">{b.skipped || 0}</td><td className="px-4 py-3">{b.uploaded_by || '-'}</td><td className="px-4 py-3">{fmtDate(b.completed_at)}</td><td className="px-4 py-3"><Badge value={b.status} /></td></tr>) : <tr><td colSpan="9" className="px-4 py-10 text-center text-slate-500">Belum ada riwayat import.</td></tr>}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
